import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getSocket } from "../../socket/socket.js";
import { useNavigate } from "react-router-dom";
import '../chatcss/Threadmin.css'
import ThreadWindow from "./MinThread.jsx";
import Loading from "../../Loader/Loader.jsx";
import { axiosClientSignIn } from "../../axios/api.js";
import ThreadTypingIndicator from "./ThreadTypingIndicator.jsx";
import { Box, Typography, Card, CardContent, Stack, Chip, Avatar, Divider, Container, Grid } from '@mui/material';

// Redux imports for thread functionality
import {
  fetchAllThreads,
  receiveThreadMessage,
  selectAllThreads,
  selectThreadLoading,
  setThreadTyping,
  clearThreadTyping
} from '../../slice/threadSlice.jsx';

const ALLThreadWindow = ({ selectedUser }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  console.log('👤 Current user:', user);
  console.log('🎯 Selected user:', selectedUser);
  
  // Redux selectors
  const allThreads = useSelector(selectAllThreads);
  const loading = useSelector(selectThreadLoading);
  const errors = useSelector((state) => state.thread.error);
  
  // Filter threads based on selectedUser
  const threads = React.useMemo(() => {
    if (!selectedUser) {
      return [];
    }
    
    // Filter threads where selectedUser is a participant
    return allThreads.filter((thread) => {
      const participants = thread.allowed || [];
      return participants.some((allowedUser) => {
        if (allowedUser.user && typeof allowedUser.user === 'object') {
          return allowedUser.user._id === selectedUser._id;
        } else {
          return allowedUser.user?.toString() === selectedUser._id?.toString();
        }
      });
    });
  }, [allThreads, selectedUser]);
  
  const socket = getSocket();

  useEffect(() => {
    // Fetch all threads when component mounts
    dispatch(fetchAllThreads());
  }, [dispatch]);
  
  // Debug: log filtered threads
  useEffect(() => {
    console.log('🧵 Filtered threads for', selectedUser?.name || 'no user', ':', threads.length, 'threads');
    console.log('All threads:', allThreads.length);
  }, [threads, selectedUser, allThreads]);

  // Listen for real-time thread messages and typing events
  useEffect(() => {
    if (!socket) return;
    
    const handleReceiveThreadMessage = (message) => {
      console.log('📨 Received thread message in list:', message);
      dispatch(receiveThreadMessage(message));
    };
    
    // Handle thread typing events for all threads
    const handleThreadTyping = (data) => {
      console.log('👀 ThreadList: Thread typing START event received:', data);
      // Dispatch to Redux store for centralized management
      if (data.userId !== user.id) {
        dispatch(setThreadTyping({
          threadId: data.threadId,
          userId: data.userId,
          senderName: data.senderName,
          isTyping: true
        }));
      }
    };
    
    const handleThreadTypingStop = (data) => {
      console.log('✋ ThreadList: Thread typing STOP event received:', data);
      if (data.userId !== user.id) {
        dispatch(clearThreadTyping({
          threadId: data.threadId,
          userId: data.userId
        }));
      }
    };
    
    socket.on('receiveThreadMessage', handleReceiveThreadMessage);
    socket.on('threadTyping', handleThreadTyping);
    socket.on('threadTypingStop', handleThreadTypingStop);
    
    return () => {
      socket.off('receiveThreadMessage', handleReceiveThreadMessage);
      socket.off('threadTyping', handleThreadTyping);
      socket.off('threadTypingStop', handleThreadTypingStop);
    };
  }, [socket, dispatch, user.id]);



  if (loading.fetchingThreads) {
    return <Loading />;
  }
  
  if (errors?.threads) {
    return (
      <div className="thread-window">
        <div className="empty-thread">
          <h1>Error loading threads</h1>
          <p>{errors.threads.error || JSON.stringify(errors.threads)}</p>
          <button onClick={() => dispatch(fetchAllThreads())}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <Box sx={{ 
      height: '100%',
      maxHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: '#f8fafc'
    }}>
      <Box sx={{ 
        flex: 1,
        overflowY: 'auto',
        p: 3,
        '&::-webkit-scrollbar': {
          width: 8
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: 'rgba(0,0,0,0.05)',
          borderRadius: 4
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'rgba(0,0,0,0.2)',
          borderRadius: 4,
          '&:hover': {
            bgcolor: 'rgba(0,0,0,0.3)'
          }
        }
      }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Box sx={{
              p: 1.5,
              borderRadius: 3,
              bgcolor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: 'primary.main',
                m: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                🧵 Thread Discussions
                {selectedUser && (
                  <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                    with {selectedUser.name}
                  </Typography>
                )}
              </Typography>
            </Box>
            
            <Chip 
              label={`${threads.length} Active ${threads.length === 1 ? 'Thread' : 'Threads'}`}
              color="primary"
              variant="filled"
              sx={{ fontWeight: 600 }}
            />
          </Stack>
          
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600 }}>
            {selectedUser 
              ? `Showing thread discussions with ${selectedUser.name}. Each thread represents a focused conversation branch from your chat.`
              : 'Select a user from the sidebar to view thread discussions with them.'
            }
          </Typography>
        </Box>

        {!selectedUser ? (
          <Card sx={{ 
            maxWidth: 600, 
            mx: 'auto', 
            mt: 8,
            borderRadius: 4,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                👥 Select a User
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                Choose someone from the user list on the left to view your thread discussions with them.
              </Typography>
              <Box sx={{ 
                fontSize: '4rem',
                opacity: 0.3,
                mb: 2
              }}>
                👤
              </Box>
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                Thread conversations will appear here once you select a contact.
              </Typography>
            </CardContent>
          </Card>
        ) : threads.length === 0 ? (
          <Card sx={{ 
            maxWidth: 600, 
            mx: 'auto', 
            mt: 8,
            borderRadius: 4,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                No Thread Discussions
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                You don't have any thread discussions with {selectedUser.name} yet. Start a conversation in the main chat and create threads!
              </Typography>
              <Box sx={{ 
                fontSize: '4rem',
                opacity: 0.3,
                mb: 2
              }}>
                🧵
              </Box>
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                Use the thread button (💬) on messages to start focused discussions.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {threads.map((item) => (
              <Grid item xs={12} key={item._id}>
                <Card sx={{
                  borderRadius: 4,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)'
                  }
                }}>
                  {/* Thread Card Header */}
                  <Box sx={{ 
                    p: 3, 
                    bgcolor: 'white',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                          <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'success.main',
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                              '0%': { opacity: 1 },
                              '50%': { opacity: 0.5 },
                              '100%': { opacity: 1 }
                            }
                          }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            Thread Discussion
                          </Typography>
                          <Chip 
                            label={`ID: ${item._id.slice(-6)}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Stack>
                        
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                          👥 <strong>Participants:</strong> {(() => {
                            const names = item.allowed?.map((allowedUser) => {
                              console.log('🔍 Debug allowed user:', allowedUser);
                              if (allowedUser.user && typeof allowedUser.user === 'object') {
                                return allowedUser.user.firstName || 'Unknown User';
                              } else {
                                return `User ${allowedUser.user?.toString().slice(-4) || 'Unknown'}`;
                              }
                            }) || [];
                            return names.length ? names.join(', ') : 'No participants';
                          })()}
                        </Typography>
                        
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                          {(item.allowed || []).map((allowedUser, index) => {
                            const name = allowedUser.user && typeof allowedUser.user === 'object' 
                              ? (allowedUser.user.firstName || 'Unknown')
                              : `User ${allowedUser.user?.toString().slice(-4) || 'Unknown'}`;
                            const profileSrc = allowedUser.user && typeof allowedUser.user === 'object'
                              ? (allowedUser.user.profileImage || "")
                              : "";
                            
                            return (
                              <Chip
                                key={index}
                                avatar={<Avatar src={profileSrc} sx={{ bgcolor: 'primary.main', fontSize: '0.75rem' }}>{name[0]?.toUpperCase()}</Avatar>}
                                label={name}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  bgcolor: 'primary.50',
                                  borderColor: 'primary.200',
                                  '&:hover': { bgcolor: 'primary.100' }
                                }}
                              />
                            );
                          })}
                        </Stack>
                      </Box>
                    </Stack>
                    
                    {/* Live Typing Indicator */}
                    <Box sx={{ mt: 2 }}>
                      <ThreadTypingIndicator threadId={item._id} />
                    </Box>
                  </Box>
                  
                  {/* Thread Content */}
                  <Box sx={{ bgcolor: '#fafafa' }}>
                    <ThreadWindow 
                      threadId={item.mainThread?._id} 
                      threadDataId={item._id}
                      participants={item.allowed}
                    />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default ALLThreadWindow;
